import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Card from 'react-bootstrap/Card';
import Table from 'react-bootstrap/Table';
import Alert from 'react-bootstrap/Alert';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import api from '../utils/api';
import PaymentForm from '../components/PaymentForm';
import { getStatusConfig } from '../utils/orderStatusConstants';
import './Orders.css';

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [paymentModalOrder, setPaymentModalOrder] = useState(null);
  const [ordersWithPayments, setOrdersWithPayments] = useState(new Map());
  const [ordersWithFirstInstallmentPaid, setOrdersWithFirstInstallmentPaid] = useState(new Set());

  const getPaymentMethodInfo = (order) => {
    if (!order) {
      return { type: 'cash', label: '-', note: '' };
    }
    const method = order.payment_method ? String(order.payment_method).toLowerCase() : '';
    if (method.includes('cod') || method.includes('delivery') || method.includes('ปลายทาง')) {
      return {
        type: 'cod',
        label: 'เก็บเงินปลายทาง',
        note: 'ชำระเมื่อรับสินค้า'
      };
    }
    if (method.includes('install')) {
      return { type: 'installment', label: 'ผ่อนชำระ', note: '' };
    }
    return { type: 'cash', label: 'ชำระเต็มจำนวน', note: '' };
  };

  const isInstallmentOrder = useCallback((order) => getPaymentMethodInfo(order).type === 'installment', []);

  const fetchOrders = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setIsLoading(false);
        return;
      }
      const response = await api.get('/api/customer/orders', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setOrders(response.data);
      
      // ตรวจสอบ pending payments สำหรับแต่ละ order (แสดงข้อความเฉพาะเมื่อยังรออนุมัติ)
      const paymentsMap = new Map();
      const firstInstallmentPaidSet = new Set();
      await Promise.all(
        response.data.map(async (order) => {
          if (order.order_status === 'awaiting_payment') {
            try {
              const orderDetailResponse = await api.get(`/api/orders/${order.order_id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              const orderDetail = orderDetailResponse.data;
              
              if (orderDetail.payments) {
                const isInstallment = isInstallmentOrder(order);
                
                if (isInstallment) {
                  // สำหรับ installment: ตรวจสอบว่ามี pending payment สำหรับงวดแรกหรือไม่
                  const firstInstallment = orderDetail.installments?.find(
                    inst => inst.installment_number === 1
                  );
                  
                  if (firstInstallment) {
                    // ตรวจสอบว่าการชำระงวดแรกเสร็จแล้วหรือยัง
                    if (firstInstallment.payment_status === 'paid') {
                      firstInstallmentPaidSet.add(order.order_id);
                    } else {
                      // ตรวจสอบว่ามี pending payment สำหรับงวดแรกหรือไม่
                      const firstInstallmentPayments = orderDetail.payments.filter(
                        p => p.installment_id === firstInstallment.installment_id && 
                             p.status === 'pending'
                      );
                      if (firstInstallmentPayments.length > 0) {
                        paymentsMap.set(order.order_id, firstInstallmentPayments);
                      }
                    }
                  }
                } else {
                  // สำหรับ cash: ตรวจสอบว่ามี pending payment หรือไม่
                  const fullPayments = orderDetail.payments.filter(
                    p => !p.installment_id && p.status === 'pending'
                  );
                  if (fullPayments.length > 0) {
                    paymentsMap.set(order.order_id, fullPayments);
                  }
                }
              }
            } catch (err) {
              // Ignore errors for individual order details
            }
          }
        })
      );
      setOrdersWithPayments(paymentsMap);
      setOrdersWithFirstInstallmentPaid(firstInstallmentPaidSet);
      
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching orders:', error);
      // ถ้าเป็น 401 หรือ 403 แสดงว่า token หมดอายุหรือไม่ถูกต้อง
      if (error.response?.status === 401 || error.response?.status === 403) {
        // ลบ token ที่หมดอายุ
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        // Redirect ไปหน้า login (Route guard จะจัดการ)
        window.location.href = '/login';
      }
      setIsLoading(false);
    }
  }, [isInstallmentOrder]);

  useEffect(() => {
    // ตรวจสอบ token ก่อนเรียก API
    const token = localStorage.getItem('token');
    if (token) {
    fetchOrders();
    } else {
      setIsLoading(false);
      // ถ้าไม่มี token จะถูก redirect โดย Route guard ใน App.js
    }
  }, [fetchOrders]);

  const getStatusBadge = (order) => {
    // ถ้าเป็น installment order และงวดแรกชำระแล้ว ให้แสดงสถานะเป็น "กำลังจัดส่ง"
    let displayStatus = order.order_status;
    if (isInstallmentOrder(order) && ordersWithFirstInstallmentPaid.has(order.order_id)) {
      displayStatus = 'waiting_for_delivery';
    }
    
    const config = getStatusConfig(displayStatus);
    const classes = ['orders-status-badge'];
    if (displayStatus === 'pending') {
      classes.push('orders-status-badge--pending');
    }
    if (displayStatus === 'awaiting_payment') {
      classes.push('orders-status-badge--awaiting');
    }
    if (displayStatus === 'waiting_for_delivery') {
      classes.push('orders-status-badge--shipping');
    }
    if (displayStatus === 'completed') {
      classes.push('orders-status-badge--completed');
    }
    if (displayStatus === 'cancelled' || displayStatus === 'cancelled_by_customer') {
      classes.push('orders-status-badge--cancelled');
    }
    return (
      <span
        className={`badge px-3 py-2 rounded-pill fw-semibold ${classes.join(' ')}`}
        style={{ 
          backgroundColor: config.color,
          color: config.textColor,
          border: 'none',
          display: 'inline-block'
        }}
      >
        {config.label}
      </span>
    );
  };

  const filterConfigs = useMemo(
    () => ({
      all: {
        label: 'รายการทั้งหมด',
        predicate: () => true
      },
      pending: {
        label: 'รออนุมัติ',
        predicate: (order) => order.order_status === 'pending'
      },
      awaiting_payment: {
        label: 'รอชำระเงิน',
        predicate: (order) => order.order_status === 'awaiting_payment'
      },
      waiting_for_delivery: {
        label: 'กำลังจัดส่ง',
        predicate: (order) => order.order_status === 'waiting_for_delivery'
      },
      completed: {
        label: 'จัดส่งสำเร็จ',
        predicate: (order) => order.order_status === 'completed'
      },
      cancelled: {
        label: 'ยกเลิกแล้ว',
        predicate: (order) =>
          ['cancelled', 'cancelled_by_customer'].includes(order.order_status)
      }
    }),
    []
  );

  const filteredOrders = useMemo(() => {
    const filter = filterConfigs[activeFilter];
    if (!filter) {
      return orders;
    }
    return orders.filter(filter.predicate);
  }, [orders, activeFilter, filterConfigs]);

  const filterCounts = useMemo(() => {
    const counts = Object.fromEntries(Object.keys(filterConfigs).map((key) => [key, 0]));
    orders.forEach((order) => {
      Object.entries(filterConfigs).forEach(([key, config]) => {
        if (config.predicate(order)) {
          counts[key] += 1;
        }
      });
    });
    return counts;
  }, [orders, filterConfigs]);

  const renderFilterSelect = () => (
    <div className="orders-filters">
      <div className="orders-filter-select-wrapper">
        <Form.Select
          value={activeFilter}
          className="orders-filter-select"
          onChange={(event) => setActiveFilter(event.target.value)}
        >
          {Object.entries(filterConfigs).map(([key, config]) => {
            const count = filterCounts[key] || 0;
            return (
              <option key={key} value={key}>
                {config.label} ({count})
              </option>
            );
          })}
        </Form.Select>
        <i className="bi bi-chevron-down orders-filter-icon"></i>
      </div>
    </div>
  );

  const handleOpenPaymentModal = (order) => {
    setPaymentModalOrder(order);
  };

  // คำนวณจำนวนเงินที่ต้องชำระสำหรับ installment order
  const getPaymentAmount = (order) => {
    if (!order) return null;
    
    // ถ้าเป็น installment order และ status = 'awaiting_payment' ใช้ monthly_payment
    if (isInstallmentOrder(order) && order.order_status === 'awaiting_payment') {
      return order.monthly_payment || order.total_amount;
    }
    
    // ถ้าไม่ใช่ installment หรือ status อื่นๆ ใช้ total_amount
    return order.total_amount;
  };

  // หา installment number ที่ต้องชำระ (สำหรับ installment order)
  const getInstallmentNumber = (order) => {
    if (!isInstallmentOrder(order) || order.order_status !== 'awaiting_payment') {
      return null;
    }
    // สำหรับ awaiting_payment มักจะเป็นงวดแรก
    return 1;
  };

  const handleClosePaymentModal = () => {
    setPaymentModalOrder(null);
    // ถ้าปิด modal โดยไม่ได้ submit (ยกเลิก) ให้ลบ order_id ออกจาก submittedOrderIds
    // แต่ถ้า submit สำเร็จแล้ว onSuccess จะ refresh ข้อมูลและปุ่มจะไม่แสดงอยู่แล้ว
  };

  const handlePaymentSuccess = () => {
    // Refresh orders after successful payment submission
    fetchOrders();
  };

  const formatCurrency = (value) => {
    if (value == null || Number.isNaN(Number(value))) {
      return '-';
    }
    return `฿${Number(value).toLocaleString('th-TH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const renderInstallmentInfo = (order) => {
    if (isInstallmentOrder(order)) {
      return `${order.installment_periods} งวด`;
    }
    return 'ชำระเต็มจำนวน';
  };

  const renderMonthlyPayment = (order) => {
    if (isInstallmentOrder(order)) {
      return formatCurrency(order.monthly_payment);
    }
    return '-';
  };


  const renderPaymentMethod = (order) => getPaymentMethodInfo(order).label;

  if (isLoading) {
    return (
      <Container className="my-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </Container>
    );
  }

  if (orders.length === 0) {
    return (
      <Container className="my-5">
        <Alert variant="info">
          <h4>ยังไม่มีคำสั่งซื้อ</h4>
          <p>เริ่มซื้อสินค้าเลย!</p>
        </Alert>
      </Container>
    );
  }

  return (
    <section className="orders-page">
      <Container className="orders-container my-5">
      <h2 className="orders-title">คำสั่งซื้อของฉัน</h2>
      {orders.length > 0 && renderFilterSelect()}

      <Card>
        <Card.Body>
          <Table responsive hover className="orders-table">
            <thead>
              <tr>
                <th>เลขที่คำสั่งซื้อ</th>
                <th>วันที่</th>
                <th>ยอดรวม</th>
                <th>วิธีชำระ</th>
                <th>จำนวนงวด</th>
                <th>ยอดผ่อน/เดือน</th>
                <th>สถานะ</th>
                <th className="text-end">จัดการ</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.length > 0 ? (
                filteredOrders.map((order) => (
                  <tr key={order.order_id}>
                    <td>#{order.order_id}</td>
                    <td>{new Date(order.order_date).toLocaleDateString('th-TH')}</td>
                    <td>{formatCurrency(order.total_amount)}</td>
                    <td>{renderPaymentMethod(order)}</td>
                    <td>{renderInstallmentInfo(order)}</td>
                    <td>{renderMonthlyPayment(order)}</td>
                    <td>{getStatusBadge(order)}</td>
                    <td>
                      {order.order_status === 'awaiting_payment' && ordersWithPayments.has(order.order_id) ? (
                        <div className="orders-actions orders-actions--centered">
                          <div className="orders-payment-submitted-text">
                            ส่งสลิปชำระเงินแล้ว
                          </div>
                          <Link to={`/orders/${order.order_id}`} className="btn btn-sm btn-outline-primary">
                            ดูรายละเอียด
                          </Link>
                        </div>
                      ) : (
                      <div className="orders-actions">
                          {order.order_status === 'awaiting_payment' && 
                           !ordersWithPayments.has(order.order_id) && 
                           !(isInstallmentOrder(order) && ordersWithFirstInstallmentPaid.has(order.order_id)) && (
                          <Button
                            variant="primary"
                            className="orders-pay-btn"
                            size="sm"
                            onClick={() => handleOpenPaymentModal(order)}
                          >
                            <i className="bi bi-qr-code me-2" />
                            {isInstallmentOrder(order) ? 'ชำระเงิน (งวดที่ 1)' : 'ชำระเงิน'}
                          </Button>
                        )}
                        <Link to={`/orders/${order.order_id}`} className="btn btn-sm btn-outline-primary">
                          ดูรายละเอียด
                        </Link>
                      </div>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="orders-empty-state">
                    ยังไม่มีคำสั่งซื้อในหมวดนี้
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
      </Container>

      <PaymentForm
        show={!!paymentModalOrder}
        onHide={handleClosePaymentModal}
        orderId={paymentModalOrder?.order_id}
        amount={getPaymentAmount(paymentModalOrder)}
        installmentNumber={getInstallmentNumber(paymentModalOrder)}
        totalInstallments={isInstallmentOrder(paymentModalOrder) ? paymentModalOrder?.installment_periods : null}
        onSuccess={handlePaymentSuccess}
      />
    </section>
  );
}

